import { diffLines } from 'diff';
import { CodingStatusType } from '../../../../api';
import { ITimelineEventStatusTagTone } from '../claim-timeline.types';
import { Coding, ICodingDiffCodeLine, ICodingDiffDisplayLine, ICodingDiffRegion, ICodingDiffResult, ICodingFieldChange, ICodingRule } from './coding-detail.types';

type CodingResult = NonNullable<Coding['before']>;

export function codingStatusToTone(status?: CodingStatusType): ITimelineEventStatusTagTone {
    switch (status) {
        case CodingStatusType.COMPLETED:
            return 'success';
        case CodingStatusType.SUBMITTED:
            return 'pending';
        case CodingStatusType.ERROR:
            return 'attention';
        case CodingStatusType.CANCELLED:
            return 'neutral';
        default:
            return 'neutral';
    }
}

export function codingHeadlineTitle(status?: CodingStatusType): string {
    switch (status) {
        case CodingStatusType.COMPLETED:
            return 'Coding Completed';
        case CodingStatusType.SUBMITTED:
            return 'Coding Submitted';
        case CodingStatusType.ERROR:
            return 'Coding Error';
        case CodingStatusType.CANCELLED:
            return 'Coding Cancelled';
        default:
            return 'Coding';
    }
}

export function getCodingPayload(coding: Coding, phase: 'before' | 'after'): unknown {
    const result: CodingResult | undefined = phase === 'before' ? coding.before : coding.after;
    if (!result) {
        return undefined;
    }

    const requestType = 'requestType' in coding ? coding.requestType : undefined;

    if (requestType) {
        switch (requestType) {
            case 'SupportCreateClaimProfessionalPayload':
                return result.supportProfessional;
            case 'ProviderCreateClaimProfessionalPayload':
                return result.providerProfessional;
            case 'SupportCreateClaimInstitutionalPayload':
                return result.supportInstitutional;
            case 'ProviderCreateClaimInstitutionalPayload':
                return result.providerInstitutional;
            default:
                break;
        }
    }

    return (
        result.providerProfessional ??
        result.supportProfessional ??
        result.providerInstitutional ??
        result.supportInstitutional
    );
}

function coerceRuleNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const asNumber = Number(value);
        if (Number.isFinite(asNumber)) {
            return asNumber;
        }
        const match = value.match(/Rule\s+(\d+)/i) || value.match(/(\d+)/);
        if (match) {
            return Number(match[1]);
        }
    }
    return null;
}

function extractRuleNumberFromObject(obj: Record<string, unknown>): number | null {
    const candidates = [
        obj.ruleNumber,
        obj.coderRuleNumber,
        obj.codingRuleNumber,
        obj.n,
        obj.number,
        // Only treat `rule` as a number-like value; nested rule objects are handled separately
        typeof obj.rule === 'number' || typeof obj.rule === 'string' ? obj.rule : undefined,
    ];

    for (const candidate of candidates) {
        const n = coerceRuleNumber(candidate);
        if (n !== null) {
            return n;
        }
    }

    if (obj.rule && typeof obj.rule === 'object') {
        return extractRuleNumberFromObject(obj.rule as Record<string, unknown>);
    }

    return null;
}

function isRuleLikeObject(obj: Record<string, unknown>): boolean {
    return (
        extractRuleNumberFromObject(obj) !== null ||
        typeof obj.text === 'string' ||
        typeof obj.message === 'string' ||
        typeof obj.description === 'string' ||
        typeof obj.ruleText === 'string'
    );
}

function ruleNumberFromKey(key: string, fallbackIndex: number): number {
    if (/^\d+$/.test(key)) {
        return Number(key);
    }
    const match = key.match(/rule\s*[_-]?\s*(\d+)/i);
    return match ? Number(match[1]) : fallbackIndex + 1;
}

function normalizeRule(item: unknown, index: number): ICodingRule {
    if (typeof item === 'string') {
        const match = item.match(/^Rule\s+(\d+)\s*:\s*(.*)$/is);
        if (match) {
            return { n: Number(match[1]), text: match[2].trim() || item };
        }
        const looseMatch = item.match(/Rule\s+(\d+)/i);
        return { n: looseMatch ? Number(looseMatch[1]) : index + 1, text: item };
    }

    if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const n = extractRuleNumberFromObject(obj) ?? index + 1;
        const text = String(
            obj.text ?? obj.message ?? obj.description ?? obj.ruleText ?? obj.name ?? JSON.stringify(item),
        );
        return { n, text };
    }

    return { n: index + 1, text: String(item) };
}

/**
 * Split plain-text coding notes like:
 * "Rule 23: ...\nRule 3: ...\nRule 30: ..."
 * into separate rules. Also handles multiple Rule N: segments on one line.
 */
function parseRulesFromPlainText(text: string, fallbackStartIndex = 0): ICodingRule[] {
    const trimmed = text.trim();
    if (!trimmed) {
        return [];
    }

    const segments = trimmed
        .split(/\r?\n/)
        .flatMap((line) =>
            line
                .split(/(?=Rule\s+\d+\s*:)/i)
                .map((part) => part.trim())
                .filter(Boolean),
        );

    return segments.map((segment, index) => normalizeRule(segment, fallbackStartIndex + index));
}

export function parseCodingNotes(notes: unknown): ICodingRule[] {
    let parsed: unknown = notes;
    if (!parsed) {
        return [];
    }

    if (typeof parsed === 'string') {
        const rawNotes = parsed;
        try {
            parsed = JSON.parse(parsed);
            if (typeof parsed === 'string') {
                try {
                    parsed = JSON.parse(parsed);
                } catch {
                    // keep single-level string
                }
            }
        } catch {
            return parseRulesFromPlainText(rawNotes);
        }
    }

    if (typeof parsed === 'string') {
        return parseRulesFromPlainText(parsed);
    }

    if (Array.isArray(parsed)) {
        // Keep fallback numbering sequential across array elements. Calling
        // parseRulesFromPlainText per string with a fresh fallback of 0 made
        // ["foo", "bar"] both become Rule 1.
        const rules: ICodingRule[] = [];
        for (const item of parsed) {
            if (typeof item === 'string') {
                rules.push(...parseRulesFromPlainText(item, rules.length));
            } else {
                rules.push(normalizeRule(item, rules.length));
            }
        }
        return rules;
    }

    if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        if (Array.isArray(obj.rules)) {
            return parseCodingNotes(obj.rules);
        }
        if (Array.isArray(obj.appliedRules)) {
            return parseCodingNotes(obj.appliedRules);
        }
        if (Array.isArray(obj.ruleApplications)) {
            return parseCodingNotes(obj.ruleApplications);
        }

        // Single rule object, e.g. { ruleNumber: 5, text: "..." } — must not fall through to Rule 1
        if (isRuleLikeObject(obj) && extractRuleNumberFromObject(obj) !== null) {
            return [normalizeRule(obj, 0)];
        }

        const entries = Object.entries(obj);
        if (entries.length > 0 && entries.every(([key]) => /^\d+$/.test(key) || /^rule/i.test(key))) {
            return entries.map(([key, value], index) => ({
                n: ruleNumberFromKey(key, index),
                text: typeof value === 'string' ? value : JSON.stringify(value),
            }));
        }

        if (isRuleLikeObject(obj)) {
            return [normalizeRule(obj, 0)];
        }
    }

    return [];
}

function humanizeFieldName(field: string): string {
    const last = field.split('.').pop() || field;
    const withoutIndex = last.replace(/\[\d+\]/g, '');
    return withoutIndex
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (char) => char.toUpperCase())
        .trim();
}

function flattenPayload(obj: unknown, prefix = ''): Record<string, string> {
    const out: Record<string, string> = {};
    if (obj === null || obj === undefined) {
        return out;
    }

    if (Array.isArray(obj)) {
        obj.forEach((item, index) => Object.assign(out, flattenPayload(item, `${prefix}[${index}]`)));
        return out;
    }

    if (typeof obj === 'object') {
        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
            const path = prefix ? `${prefix}.${key}` : key;
            if (value !== null && typeof value === 'object') {
                Object.assign(out, flattenPayload(value, path));
            } else {
                out[path] = value == null ? '' : String(value);
            }
        }
        return out;
    }

    if (prefix) {
        out[prefix] = String(obj);
    }
    return out;
}

export function computeCodingFieldChanges(before: unknown, after: unknown): ICodingFieldChange[] {
    const beforeFlat = flattenPayload(before);
    const afterFlat = flattenPayload(after);
    const changes: ICodingFieldChange[] = [];

    for (const [field, value] of Object.entries(afterFlat)) {
        if (!(field in beforeFlat)) {
            changes.push({ op: 'added', label: humanizeFieldName(field), field, value });
        } else if (beforeFlat[field] !== value) {
            changes.push({ op: 'changed', label: humanizeFieldName(field), field, value });
        }
    }

    for (const field of Object.keys(beforeFlat)) {
        if (!(field in afterFlat)) {
            changes.push({
                op: 'removed',
                label: humanizeFieldName(field),
                field,
                value: beforeFlat[field],
            });
        }
    }

    return changes.sort((a, b) => a.field.localeCompare(b.field));
}

export function formatCodingChangesSummary(changes: ICodingFieldChange[]): string {
    const added = changes.filter((change) => change.op === 'added').length;
    const changed = changes.filter((change) => change.op === 'changed').length;
    const removed = changes.filter((change) => change.op === 'removed').length;
    const parts: string[] = [];

    if (added) {
        parts.push(`${added} field${added === 1 ? '' : 's'} added`);
    }
    if (changed) {
        parts.push(`${changed} field${changed === 1 ? '' : 's'} changed`);
    }
    if (removed) {
        parts.push(`${removed} field${removed === 1 ? '' : 's'} removed`);
    }

    return parts.join(' · ') || 'no field changes';
}

function buildAfterDiffLines(beforeText: string, afterText: string): ICodingDiffCodeLine[] {
    const parts = diffLines(beforeText, afterText);
    const allLines: ICodingDiffCodeLine[] = [];
    let lineNum = 1;

    for (const part of parts) {
        if (part.removed) {
            continue;
        }

        let chunk = part.value;
        if (chunk.endsWith('\n')) {
            chunk = chunk.slice(0, -1);
        }

        const lines = chunk === '' ? [] : chunk.split('\n');
        for (const text of lines) {
            allLines.push({
                kind: 'code',
                n: lineNum,
                text,
                added: !!part.added,
            });
            lineNum++;
        }
    }

    return allLines;
}

function groupAddedIndices(addedIndices: number[], maxGap: number): Array<{ start: number; end: number }> {
    if (addedIndices.length === 0) {
        return [];
    }

    const groups: Array<{ start: number; end: number }> = [];
    let groupStart = addedIndices[0];
    let groupEnd = addedIndices[0];

    for (let index = 1; index < addedIndices.length; index += 1) {
        const current = addedIndices[index];
        if (current - groupEnd > maxGap) {
            groups.push({ start: groupStart, end: groupEnd });
            groupStart = current;
        }
        groupEnd = current;
    }

    groups.push({ start: groupStart, end: groupEnd });
    return groups;
}

function buildRegionSegments(
    allLines: ICodingDiffCodeLine[],
    groups: Array<{ start: number; end: number }>,
    contextLines: number,
): { segments: ICodingDiffDisplayLine[]; regions: ICodingDiffRegion[] } {
    const segments: ICodingDiffDisplayLine[] = [];
    const regions: ICodingDiffRegion[] = [];
    let previousSliceEnd = -1;

    groups.forEach((group, regionId) => {
        const sliceStart = Math.max(0, group.start - contextLines);
        const sliceEnd = Math.min(allLines.length - 1, group.end + contextLines);

        if (sliceStart > previousSliceEnd + 1) {
            const fromLine = previousSliceEnd >= 0 ? allLines[previousSliceEnd].n + 1 : 1;
            const toLine = allLines[sliceStart].n - 1;
            if (toLine >= fromLine) {
                segments.push({
                    kind: 'ellipsis',
                    label:
                        fromLine === toLine
                            ? `line ${fromLine} unchanged`
                            : `lines ${fromLine}–${toLine} unchanged`,
                });
            }
        }

        const segmentIndex = segments.length;
        for (let lineIndex = sliceStart; lineIndex <= sliceEnd; lineIndex += 1) {
            segments.push(allLines[lineIndex]);
        }

        regions.push({
            id: regionId,
            firstAddedLine: allLines[group.start].n,
            allLineIndex: group.start,
            segmentIndex,
        });

        previousSliceEnd = sliceEnd;
    });

    if (previousSliceEnd >= 0 && previousSliceEnd < allLines.length - 1) {
        const fromLine = allLines[previousSliceEnd].n + 1;
        const toLine = allLines[allLines.length - 1].n;
        segments.push({
            kind: 'ellipsis',
            label:
                fromLine === toLine
                    ? `line ${fromLine} unchanged`
                    : `lines ${fromLine}–${toLine} unchanged`,
        });
    }

    return { segments, regions };
}

export function buildCodingDiffLines(
    beforePayload: unknown,
    afterPayload: unknown,
    contextLines = 6,
): ICodingDiffResult {
    const beforeText = beforePayload != null ? JSON.stringify(beforePayload, null, 2) : '';
    const afterText = afterPayload != null ? JSON.stringify(afterPayload, null, 2) : '';
    const emptyResult: ICodingDiffResult = { segments: [], regions: [], allLines: [] };

    if (!afterText) {
        return emptyResult;
    }

    const allLines = buildAfterDiffLines(beforeText, afterText);
    const addedIndices = allLines
        .map((line, index) => (line.added ? index : -1))
        .filter((index) => index >= 0);

    if (addedIndices.length === 0) {
        return {
            segments: allLines.slice(0, Math.min(allLines.length, 20)),
            regions: [],
            allLines,
        };
    }

    const groups = groupAddedIndices(addedIndices, contextLines * 2 + 1);
    const { segments, regions } = buildRegionSegments(allLines, groups, contextLines);

    return { segments, regions, allLines };
}
